import type { FieldMeta, FieldOption, PrintSettings } from '../api/types';
import { formatValue, formatUnknownValue } from '../utils/formatValue';
import { printPageChromeCss, wrapPrintBody } from '../utils/printPage';

export interface PrintRow {
  recordId: string;
  /** keyed by fieldId */
  cells: Record<string, unknown>;
}

export interface BuildHtmlInput {
  fields: FieldOption[];
  fieldMetas: FieldMeta[];
  rows: PrintRow[];
  settings: PrintSettings;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function paperToCss(size: PrintSettings['paperSize']): string {
  return size;
}

export function buildPrintHtml({ fields, fieldMetas, rows, settings }: BuildHtmlInput): string {
  const checked = fields
    .filter((f) => f.checked)
    .sort((a, b) => a.order - b.order);

  const metaById = new Map(fieldMetas.map((m) => [m.id, m]));

  const title = settings.title || '打印预览';
  // @page margin:0 = 关闭浏览器打印页眉/页脚；页边距由 printPage 骨架补回
  const css = `
    @page { size: ${paperToCss(settings.paperSize)} ${settings.orientation}; margin: 0; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Helvetica Neue', Arial, sans-serif; color: #000; }
    body { padding: ${settings.marginMm}mm; font-size: ${settings.fontSizePx}px; }
    h1 { text-align: center; font-size: ${Math.round(settings.fontSizePx * 1.6)}px; margin: 0 0 12px; font-weight: 600; }
    table { width: 100%; border-collapse: collapse; table-layout: auto; }
    th, td { border: 1px solid #999; padding: 4px 6px; vertical-align: top; word-break: break-word; }
    th { background: #f5f5f5; font-weight: 600; text-align: left; }
    tr { page-break-inside: avoid; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    .idx { width: 36px; text-align: right; color: #666; }
    @media print {
      .no-print { display: none !important; }
    }
    ${printPageChromeCss(settings.marginMm)}
  `;

  const headerCells: string[] = [];
  if (settings.showIndex) headerCells.push('<th class="idx">#</th>');
  for (const f of checked) {
    headerCells.push(`<th>${escapeHtml(f.name)}</th>`);
  }

  const bodyRows: string[] = [];
  rows.forEach((row, i) => {
    const cells: string[] = [];
    if (settings.showIndex) cells.push(`<td class="idx">${i + 1}</td>`);
    for (const f of checked) {
      const meta = metaById.get(f.id);
      const raw = row.cells[f.id];
      const text = meta ? formatValue(meta, raw) : formatUnknownValue(raw);
      cells.push(`<td>${escapeHtml(text)}</td>`);
    }
    bodyRows.push(`<tr>${cells.join('')}</tr>`);
  });

  if (bodyRows.length === 0) {
    const colspan = checked.length + (settings.showIndex ? 1 : 0);
    bodyRows.push(
      `<tr><td colspan="${colspan}" style="text-align:center;color:#999;padding:24px;">无记录</td></tr>`,
    );
  }

  return `<!doctype html>
<html lang="zh-CN">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${css}</style>
</head>
<body>
${wrapPrintBody(`${settings.title ? `<h1>${escapeHtml(settings.title)}</h1>` : ''}
<table>
<thead><tr>${headerCells.join('')}</tr></thead>
<tbody>${bodyRows.join('')}</tbody>
</table>`)}
</body>
</html>`;
}
